<template>
    <v-row dense>
        <v-col cols="12" md="12">
            <v-card variant="outlined">
                <v-card-title>
                    <span class="font-weight-bold text-subtitle-2">
                        {{ $t('discount_on') }}
                    </span>
                </v-card-title>
                <v-card-text class="text-body-1">
                    <v-tabs v-model="discountOnActiveTab" class="mb-4">
                        <v-tab v-for="tab in discountOnTabs" :key="tab.name" :value="tab.name">
                            {{ tab.label }}
                        </v-tab>
                    </v-tabs>
                    <v-tabs-window v-model="discountOnActiveTab">
                        <v-tabs-window-item value="tab-membership">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.membershipLevels"
                                        :headers="useAllSelectedHeaders"
                                        :items="useMembershipLevelsItems"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                    />
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-citizenship">
                            <div class="mb-4 text-subtitle-1">
                                {{ $t('discount_choose_countries') }}
                            </div>
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.limitsOnCitizenship"
                                        :headers="useAllSelectedHeaders"
                                        :items="useCountries"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="useCountries.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    />
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-device">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.deviceTypes"
                                        :headers="useAllSelectedHeaders"
                                        :items="useDeviceTypeItems"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                    />
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-guest">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.paxesAvailability"
                                        :headers="useAllSelectedHeaders"
                                        :items="usePaxes"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="usePaxes.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    >
                                        <template v-slot:item.name="{item}">
                                            {{ localized(item.name) }}
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-room">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.servicesAvailability"
                                        :headers="useAllSelectedHeaders"
                                        :items="useRoomTypes"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="useRoomTypes.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    />
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-meal">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.secondaryServicesAvailability"
                                        :headers="useAllSelectedHeaders"
                                        :items="useMealTypes"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="useMealTypes.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    >
                                        <template v-slot:item.name="{item}">
                                            {{ localized(item.name) }}
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                        <v-tabs-window-item value="tab-allocation">
                            <v-row no-gutters>
                                <v-col cols="12" md="6">
                                    <v-data-table
                                        v-model="discountForEdit.allocationsAvailability"
                                        :headers="useAllSelectedHeaders"
                                        :items="useAllocationTypeItems"
                                        disable-sort
                                        density="compact"
                                        class="elevation-0 table-no-border"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                    />
                                </v-col>
                            </v-row>
                        </v-tabs-window-item>
                    </v-tabs-window>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import {contractServiceType} from '~/utils/types/types'

    const props = defineProps<{
        serviceType: string
    }>()

    const {membershipLevels} = useMembershipLevels()
    const localized = useLocalized()
    const discountOnActiveTab = ref<string>()

    const discountForEdit = defineModel<any>()

    const {t} = useI18n()

    const discountOnTabs = computed(() => {
        const discountOnTabs = [
            {
                name: 'tab-membership',
                label: t('membership_levels'),
            },
            {
                name: 'tab-citizenship',
                label: t('citizenship_levels'),
            },
            {
                name: 'tab-device',
                label: t('device_type'),
            },
        ]
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return [
                    ...[
                        {name: 'tab-guest', label: t('product.guest_type')},
                        {name: 'tab-room', label: t('product.room_type')},
                        {name: 'tab-allocation', label: t('product.allocation_type')},
                        {name: 'tab-meal', label: t('product.meal_type')},
                    ],
                    ...discountOnTabs,
                ]
            default:
                return discountOnTabs
        }
    })

    const useCountries = computed(() => {
        return runtimeStore.countries
    })
    const useMealTypes = computed(() => {
        return supplierStore.mealTypes.filter(({active}) => active)
    })
    const useRoomTypes = computed(() => {
        return supplierStore.productServices.filter(({active}) => active)
    })
    const useMembershipLevelsItems = computed(() => {
        return membershipLevels.value.map(({code}) => ({id: code, name: t(`discount_membership.${code}`)}))
    })
</script>
